import { getLayoutVideoUrl, resolveCloudVideoUrl } from "../../../lib/utils";

/**
 * Builds a reusable Basic Driving Controls tutorial for any mission
 * Handles deceleration (Z), acceleration (/), and reveal (Space) controls
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {Object} tutorialConfig The configuration object for the tutorial
 * @param {String} tutorialStimulus The HTML stimulus to show
 * @param {String} controlType The type of control: 'decel', 'accel', or 'reveal'
 * @param {Number} stepNumber Optional step number (1, 2, or 3) for title (default: auto-detected from controlType)
 * @param {Function} buildTaskWithPhasesCustom The mission-specific builder function (e.g., buildMissionZeroTaskWithPhasesCustom)
 * @returns A jsPsych node with the tutorial phases
 */
export function buildBasicControlsTutorial(
  jsPsych,
  metadata,
  tutorialConfig,
  tutorialStimulus,
  controlType,
  buildTaskWithPhasesCustom,
  stepNumber = null
) {
  const controlConfigs = {
    decel: {
      phases: [0, 1],
      agentFile: "footer_LaneLabels_speedDown.svg",
      disableAcceleration: true,
      hideTargetCar: true,
      defaultStepNumber: 1,
      instruction: "Press '__BACKWARD_KEY__' to slow down until you reach minimum speed",
      autoComplete: { autoCompleteAtMinSpeed: true },
      videoUrl: () => getLayoutVideoUrl("instructions/videos/basicControls/speed_slow.mp4"),
    },
    accel: {
      phases: [0, 1],
      agentFile: "footer_LaneLabels_speedUp.svg",
      disableDeceleration: true,
      hideTargetCar: true,
      defaultStepNumber: 2,
      instruction: "Press '__FORWARD_KEY__' to speed up until you reach maximum speed",
      autoComplete: { autoCompleteAtMaxSpeed: true },
      videoUrl: () => getLayoutVideoUrl("instructions/videos/basicControls/speed_up.mp4"),
    },
    reveal: {
      phases: [0, 1, 2],
      agentFile: "footer_LaneLabels_spaceBar.svg",
      defaultStepNumber: 3,
      instruction: "Press '__SPACE_KEY__' when closer to the car to reveal",
      videoUrl: resolveCloudVideoUrl("instructions/videos/basicControls/lock.mp4"),
      addPhase2Delay: true,
    },
  };

  const config = controlConfigs[controlType];
  if (!config) {
    throw new Error(`Unknown controlType: ${controlType}. Must be 'decel', 'accel', or 'reveal'.`);
  }

  const step = stepNumber ?? config.defaultStepNumber;
  const title = `Basic Driving Controls (${step}/3)`;

  // Build options object
  const options = {
    agentFile: config.agentFile,
    tutorialHeader: {
      title,
      instruction: config.instruction,
    },
    ready: {
      imageOrVideoUrl: config.videoUrl,
    },
  };

  if (config.disableAcceleration) {
    options.disableAcceleration = true;
  }
  if (config.disableDeceleration) {
    options.disableDeceleration = true;
  }
  if (config.hideTargetCar) {
    options.hideTargetCar = true;
  }
  if (config.autoComplete) {
    options.customTrialConfigOverrides = config.autoComplete;
  }
  if (config.addPhase2Delay) {
    options.addPhase2Delay = true;
  }

  return buildTaskWithPhasesCustom(jsPsych, metadata, tutorialConfig, tutorialStimulus, config.phases, options);
}
